import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import { SharedValue } from 'react-native-reanimated';
import { useSelector } from 'react-redux';

import {
	CityPng,
	LondonPng,
	MacaoPng,
	MonacoPng,
	ParisPng,
	SofiaPng,
	VegasPng,
	VipPng,
} from '../../../assets';
import ChipCoinPng from '../../../assets/chip.png';
import baseLock from '../../../assets/main/vipButton/lock4_1.png';
import lockBg from '../../../assets/main/vipButton/lock4_2.png';
import lockEn from '../../../assets/main/vipButton/lock4_2_en.png';
import lockFr from '../../../assets/main/vipButton/lock4_2_fr.png';
import { SPINE_ASSETS } from '../../../assets/spine';
import SpineAnimation from '../../../components/SpineAnimation';
import { testProps } from '../../../config';
import i18n from '../../../i18n/i18n';
import { RootState } from '../../../store';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { selectMyTablesItems } from '../../../store/lobby/lobbySagas';
import { LobbyRoom as LobbyRoomType, MyTable, UserProfile } from '../../../store/types';
import { useDevice } from '../../../utils/hooks/useDevice';
import { RootStackParamList } from '../../navigator';
import { TouchableOpacity } from '../common/Touchable';
import { useTapGuard } from '../common/WheelCarousel/useTapGuard';
import UnlockRoomModal from '../Modals/UnlockRoomModal';
import { LobbyCardFrame } from './LobbyCardFrame';
import { LobbyPlayersBadge } from './LobbyPlayersBadge';

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const CARD_FONT = 'Sofia Sans Semi Condensed';

const DESIGN_TEXT_TOP = 4.136;

const LOCK_ASPECT = 341 / 427;

const CHIP_ART = { w: 973, h: 1024, disc: 923, discCenterY: 514.5 / 1024 };
const CHIP_COIN = 15.4;

const TEXT_CENTER_PER_FONT = 1.015 - 0.68 / 2;

const formatPlayerCount = (count: number) => {
	if (count < 1000) return String(count);

	const thousands = count / 1000;

	return `${thousands >= 10 ? Math.round(thousands) : thousands.toFixed(1)}K`;
};

const D_TALL = {
	design: { width: 226, height: 416 },
	tablesPill: { x: 34.855 / 226, y: 13 / 416, w: 155.164 / 226, h: 21.273 / 416 },
	title: { x: 12 / 226, y: 41 / 416, w: 202 / 226, h: 45 / 416, font: 32 / 416 },
	art: { x: 17 / 226, y: 73 / 416, w: 189 / 226, h: 252 / 416 },
	playersBadge: { x: 166 / 226, y: 105 / 416, w: 42 / 226 },
	row: { x: 20.238 / 226, w: 181.025 / 226, h: 34.273 / 416 },
	stakeY: 325 / 416,
	lock: { h: 83.088 / 201 },
	stakeGapLabel: 10.497 / 226,
	stakeGapChip: 11.229 / 226,
	stakeTextTop: DESIGN_TEXT_TOP / 416,
	levelY: 366.36 / 416,
	tablesFont: 13 / 416,
	tablesLine: 18.909 / 13,
	stakeFont: 18 / 416,
	levelFont: 14 / 416,
	stakeLine: 26 / 18,
	levelLine: 20.091 / 14,
	plate: { d: 184 / 226, cx: 115 / 226, cy: 192 / 416 },
};

// The carousel stage's 256x376 card (Figma 8526:39640): the same pill, title and rows, the art
// shortened to 212 and the rows widened to 212.
const D_STAGE: typeof D_TALL = {
	design: { width: 256, height: 376 },
	tablesPill: { x: 50.418 / 256, y: 13 / 376, w: 155.164 / 256, h: 21.273 / 376 },
	title: { x: 12 / 256, y: 41 / 376, w: 232 / 256, h: 45 / 376, font: 32 / 376 },
	art: { x: 42.5 / 256, y: 73 / 376, w: 171 / 256, h: 212 / 376 },
	playersBadge: { x: 180 / 256, y: 102.784 / 376, w: 42 / 256 },
	row: { x: 22 / 256, w: 212 / 256, h: 34.273 / 376 },
	stakeY: 285.4 / 376,
	lock: { h: 83.088 / 227 },
	stakeGapLabel: 10.497 / 256,
	stakeGapChip: 11.229 / 256,
	stakeTextTop: DESIGN_TEXT_TOP / 376,
	levelY: 327.664 / 376,
	tablesFont: 13 / 376,
	tablesLine: 18.909 / 13,
	stakeFont: 18 / 376,
	levelFont: 14 / 376,
	stakeLine: 26 / 18,
	levelLine: 20.091 / 14,
	plate: { d: 166.5 / 256, cx: 128 / 256, cy: 173 / 376 },
};

const SPINE_PLATE_UNITS = 572;
const SPINE_FIT_MARGIN = 0.95;
const SPINE_BOUNDS_CENTER_X = 0.5;
const SPINE_BOUNDS_FALLBACK = { size: 680, centerY: -0.5 };
const SPINE_BOUNDS: Record<string, { size: number; centerY: number }> = {
	LONDON: SPINE_BOUNDS_FALLBACK,
	SOFIA: SPINE_BOUNDS_FALLBACK,
	MONACO: SPINE_BOUNDS_FALLBACK,
	VIP: SPINE_BOUNDS_FALLBACK,
	PARIS: { size: 736.5, centerY: 27.75 },
	MACAO: { size: 710.526, centerY: 14.763 },
	LAS_VEGAS: { size: 702.673, centerY: 10.837 },
};

const CARD_RADIUS = 15;

type LobbyRoomProps = {
	index?: number;
	room: LobbyRoomType;
	totalCount?: number;
	progress?: SharedValue<number>;
	cardWidth?: number;
	cardHeight?: number;
	isFeatured?: boolean;
	framed?: boolean;
	shouldRenderSpine?: boolean;
	playing?: boolean;
	visualScale?: number;
	/** 'stage': the mobile carousel's 256x376 card; 'tall' (default): the 226x416 one. */
	shape?: 'tall' | 'stage';
};

type Nav = NativeStackNavigationProp<RootStackParamList>;

const ROOM_NAME_TRANSLATION_KEYS: Record<string, string> = {
	vip: 'T_SITE_VIP',
	sofia: 'T_SITE_SOFIA',
	london: 'T_SITE_LONDON',
	paris: 'T_SITE_PARIS',
	monaco: 'T_SITE_MONACO',
	macao: 'T_SITE_MACAO',
	vegas: 'T_SITE_VEGAS',
};

const ROOM_IMAGES: Record<string, any> = {
	paris: ParisPng,
	london: LondonPng,
	sofia: SofiaPng,
	macao: MacaoPng,
	monaco: MonacoPng,
	vegas: VegasPng,
	vip: VipPng,
};

const ROOM_SPINE_SKINS: Record<string, string> = {
	paris: 'PARIS',
	london: 'LONDON',
	sofia: 'SOFIA',
	macao: 'MACAO',
	monaco: 'MONACO',
	vegas: 'LAS_VEGAS',
	vip: 'VIP',
};

const isRoomLocked = ({
	boughtRooms,
	room,
	playerLevel = 1,
	forceUnlocked = false,
}: {
	boughtRooms?: UserProfile['user_purchases'];
	room: LobbyRoomType;
	playerLevel?: number;
	forceUnlocked?: boolean;
}) => {
	let isRoomBought = forceUnlocked;

	if (boughtRooms) {
		switch (room.name.toLowerCase()) {
			case 'sofia':
				isRoomBought = forceUnlocked || boughtRooms.sofia || false;
				break;
			case 'london':
				isRoomBought = forceUnlocked || boughtRooms.london || false;
				break;
			case 'paris':
				isRoomBought = forceUnlocked || boughtRooms.paris || false;
				break;
			case 'macao':
				isRoomBought = forceUnlocked || boughtRooms.macao || false;
				break;
			case 'monaco':
				isRoomBought = forceUnlocked || boughtRooms.monaco || false;
				break;
			case 'vegas':
				isRoomBought = forceUnlocked || boughtRooms.vegas || false;
				break;
		}
	}

	return !isRoomBought && playerLevel < room.minLevel;
};

/**
 * A room the player has levelled past (maxLevel is inclusive, e.g. Sofia is
 * levels 1-8). Unlike `isRoomLocked`, there is nothing to buy: the card is shown
 * locked and does nothing on tap. Mirrors the backend's
 * `isRoomAvailableForLevel`, which refuses both seating and the table listing.
 */
const isRoomOutgrown = ({ room, playerLevel = 1 }: { room: LobbyRoomType; playerLevel?: number }) =>
	room.maxLevel != null && playerLevel > room.maxLevel;

export const LobbyRoom = ({
	room,
	index = 0,
	totalCount = 0,
	progress,
	cardWidth,
	cardHeight,
	isFeatured = false,
	framed = false,
	shouldRenderSpine = true,
	visualScale = 1,
	playing = true,
	shape = 'tall',
}: LobbyRoomProps) => {
	const D = shape === 'stage' ? D_STAGE : D_TALL;
	const PLATE = D.plate;
	const { t } = useTranslation();
	const { navigate } = useNavigation<Nav>();
	const { safeAreaWidth, safeAreaHeight } = useDevice();
	const userPurchases = useSelector((state: RootState) => state.auth.user?.user_purchases);
	const userLevel = useSelector((state: RootState) => state.auth.user?.level);
	const isLogged = useSelector(selectIsLogged);
	const myTablesItems = useSelector(selectMyTablesItems);
	const [isJustUnlocked, setIsJustUnlocked] = useState(false);
	const [showUnlockRoomModal, setShowUnlockRoomModal] = useState(false);

	const width = (cardWidth ?? safeAreaWidth * 0.82) * visualScale;
	const height = (cardHeight ?? safeAreaHeight * 0.58) * visualScale;
	const pill = {
		left: width * D.tablesPill.x,
		top: height * D.tablesPill.y,
		width: width * D.tablesPill.w,
		height: height * D.tablesPill.h,
	};
	const artWidth = width * D.art.w;
	const artHeight = height * D.art.h;
	const lockHeight = width * D.lock.h;
	const lockWidth = lockHeight * LOCK_ASPECT;
	const lockBox = useMemo(
		() => ({ width: lockWidth, height: lockHeight }),
		[lockHeight, lockWidth],
	);
	const rowLeft = width * D.row.x;
	const rowWidth = width * D.row.w;
	const rowHeight = height * D.row.h;
	const badgeWidth = width * D.playersBadge.w;
	const roomKey = room?.name?.trim().toLowerCase();
	const roomSpineSkin = ROOM_SPINE_SKINS[roomKey] ?? null;
	const plateDiameter = width * PLATE.d;
	const spineUnit = plateDiameter / SPINE_PLATE_UNITS;
	const spineBounds = (roomSpineSkin && SPINE_BOUNDS[roomSpineSkin]) || SPINE_BOUNDS_FALLBACK;
	const spineBox = (plateDiameter * spineBounds.size) / (SPINE_PLATE_UNITS * SPINE_FIT_MARGIN);
	const spineLeft = width * PLATE.cx + SPINE_BOUNDS_CENTER_X * spineUnit - spineBox / 2;
	const spineTop = height * PLATE.cy - spineBounds.centerY * spineUnit - spineBox / 2;
	const useSpine = Boolean(roomSpineSkin) && shouldRenderSpine;

	const tableFont = clamp(height * D.tablesFont, 7, 20);
	const titleFont = clamp(height * D.title.font, 16, 48);
	const stakeFont = clamp(height * D.stakeFont, 9, 26);

	const chipHeight = (height / D.design.height) * CHIP_COIN * (CHIP_ART.h / CHIP_ART.disc);
	const chipWidth = chipHeight * (CHIP_ART.w / CHIP_ART.h);
	const chipTop =
		height * D.stakeTextTop + TEXT_CENTER_PER_FONT * stakeFont - chipHeight * CHIP_ART.discCenterY;
	const levelFont = clamp(height * D.levelFont, 8, 20);
	const cardScale = width / D.design.width;
	const roomDisplayName = ROOM_NAME_TRANSLATION_KEYS[roomKey]
		? t(ROOM_NAME_TRANSLATION_KEYS[roomKey])
		: room?.name;
	const ownedRoomTablesCount = useMemo(
		() => myTablesItems.filter((item: MyTable) => String(item.room_id) === String(room.id)).length,
		[myTablesItems, room.id],
	);
	const visibleActiveTablesCount = Math.max(room.activeTablesCount ?? 0, ownedRoomTablesCount);
	const playersCount = room.playersCount ?? 0;
	const isOutgrown = isRoomOutgrown({ room, playerLevel: userLevel });
	const isLocked =
		isOutgrown ||
		isRoomLocked({
			boughtRooms: userPurchases,
			room,
			playerLevel: userLevel,
			forceUnlocked: isJustUnlocked,
		});

	const lockImages = useMemo(() => {
		const currentLang = i18n.language || 'en';

		if (currentLang === 'fr') return { lock4_1: baseLock, lock4_2: lockFr };
		if (currentLang === 'bg' || currentLang === 'ru') return { lock4_1: baseLock, lock4_2: lockBg };

		return { lock4_1: baseLock, lock4_2: lockEn };
	}, []);

	const isWheelMoving = useTapGuard();

	const handleRoomClick = useCallback(() => {
		if (!isLogged) {
			navigate('Login');
			return;
		}

		// Outgrown rooms are locked for good: no unlock-by-purchase, no message —
		// the player was told about the level in-game and simply cannot go back.
		if (isOutgrown) return;

		if (isLocked) {
			setShowUnlockRoomModal(true);
			return;
		}

		console.log('[LOBBY_ROOM] Opening room', {
			roomId: String(room.id),
			roomName: room.name,
			roomKey,
			activeTablesCount: room.activeTablesCount,
			ownedRoomTablesCount,
			visibleActiveTablesCount,
		});

		navigate('Room', {
			roomSlug: room.name.toLowerCase(),
			roomId: String(room.id),
		});
	}, [
		isLogged,
		isLocked,
		isOutgrown,
		navigate,
		ownedRoomTablesCount,
		room,
		roomKey,
		visibleActiveTablesCount,
	]);

	const handleRoomPress = useCallback(() => {
		if (isWheelMoving()) return;

		handleRoomClick();
	}, [handleRoomClick, isWheelMoving]);

	const handleRoomUnlocked = useCallback(() => {
		setIsJustUnlocked(true);
		setShowUnlockRoomModal(false);
	}, []);

	return (
		<>
			<View style={[styles.wrapper, { width, height }]}>
				{framed ? (
					<LobbyCardFrame
						width={width}
						height={height}
						progress={progress}
						index={index}
						count={totalCount}
						designWidth={D.design.width}
					/>
				) : null}

				<View
					pointerEvents='none'
					style={[styles.artClip, { borderRadius: CARD_RADIUS * cardScale }]}
				>
					{useSpine && roomSpineSkin ? (
						<View
							style={{
								position: 'absolute',
								left: spineLeft,
								top: spineTop,
								width: spineBox,
								height: spineBox,
							}}
						>
							<SpineAnimation
								key={`${roomKey}-${Math.round(width)}-${Math.round(height)}-${isFeatured ? 'featured' : 'side'}`}
								skeletonFile={SPINE_ASSETS.cities.skeletonFile}
								atlasFile={SPINE_ASSETS.cities.atlasFile}
								animationName={
									isLocked
										? SPINE_ASSETS.cities.animations?.disabled
										: SPINE_ASSETS.cities.animations?.idle
								}
								skin={roomSpineSkin}
								loop
								playing={playing}
								sharedCanvasGroup='lobby-rooms'
								width={spineBox}
								height={spineBox}
							/>
						</View>
					) : (
						<Image
							source={ROOM_IMAGES[roomKey] ?? CityPng}
							resizeMode='contain'
							style={{
								position: 'absolute',
								left: width * D.art.x,
								top: height * D.art.y,
								width: artWidth,
								height: artHeight,
								opacity: isLocked ? 0.2 : 1,
							}}
						/>
					)}
				</View>

				<TouchableOpacity
					onPress={handleRoomPress}
					{...testProps(`cta-lobby-room-tables-${room.id}`)}
					style={[
						styles.tablesPill,
						pill,
						{ borderRadius: 20 * cardScale, borderWidth: 1 * cardScale },
						isLocked && styles.lockedDim,
					]}
				>
					<Text
						numberOfLines={1}
						adjustsFontSizeToFit
						minimumFontScale={0.7}
						style={[
							styles.tablesText,
							{ fontSize: tableFont, lineHeight: tableFont * D.tablesLine },
						]}
					>
						{visibleActiveTablesCount} {t('T_SITE_LOBBY_ROOM_TABLES')}
					</Text>
				</TouchableOpacity>

				<Text
					numberOfLines={1}
					adjustsFontSizeToFit
					minimumFontScale={0.62}
					style={[
						styles.roomTitle,
						{
							left: width * D.title.x,
							width: width * D.title.w,
							top: height * D.title.y,
							height: height * D.title.h,
							fontSize: titleFont,
							lineHeight: titleFont * 1.08,
						},
						isLocked && styles.lockedDim,
					]}
				>
					{roomDisplayName}
				</Text>

				<TouchableOpacity
					onPress={handleRoomPress}
					activeOpacity={0.9}
					{...testProps(`cta-lobby-room-open-${room.id}`)}
					style={[
						styles.artSlot,
						{
							top: height * D.art.y,
							left: width * D.art.x,
							width: artWidth,
							height: artHeight,
						},
					]}
				>
					{isLocked && (
						<View style={styles.lockpadContainer}>
							<View style={lockBox}>
								<Image
									source={lockImages.lock4_1}
									style={[styles.lockLayer, lockBox]}
									resizeMode='contain'
								/>
								<Image
									source={lockImages.lock4_2}
									style={[styles.lockLayer, lockBox]}
									resizeMode='contain'
								/>
							</View>
						</View>
					)}
				</TouchableOpacity>

				<View
					style={{
						position: 'absolute',
						left: width * D.playersBadge.x,
						top: height * D.playersBadge.y,
					}}
				>
					<LobbyPlayersBadge label={formatPlayerCount(playersCount)} width={badgeWidth} />
				</View>

				{/* Stake row */}
				<View
					style={[
						styles.stakeRow,
						{
							left: rowLeft,
							top: height * D.stakeY,
							width: rowWidth,
							height: rowHeight,
							borderRadius: 10 * cardScale,
							borderWidth: 0.2 * cardScale,
							shadowColor: '#FF7200',
							shadowOffset: { width: 0, height: 0 },
							shadowOpacity: 0.7,
							shadowRadius: 5.5 * cardScale,
						},
						isLocked && styles.lockedDim,
					]}
				>
					<Text
						numberOfLines={1}
						style={[
							styles.stakeLabel,
							{
								marginTop: height * D.stakeTextTop,
								marginRight: width * D.stakeGapLabel,
								fontSize: stakeFont,
								lineHeight: stakeFont * D.stakeLine,
							},
						]}
					>
						{t('T_Stake')}
					</Text>
					<Image
						source={ChipCoinPng}
						resizeMode='contain'
						style={{
							marginTop: chipTop,
							marginRight: width * D.stakeGapChip,
							width: chipWidth,
							height: chipHeight,
						}}
					/>
					<Text
						numberOfLines={1}
						style={[
							styles.stakeValue,
							{
								marginTop: height * D.stakeTextTop,
								fontSize: stakeFont,
								lineHeight: stakeFont * D.stakeLine,
							},
						]}
					>
						{room.entryFee}
					</Text>
				</View>

				<View
					style={[
						styles.levelRow,
						{
							left: rowLeft,
							top: height * D.levelY,
							width: rowWidth,
							height: rowHeight,
							borderRadius: 10 * cardScale,
							borderWidth: 1 * cardScale,
						},
						isLocked && styles.lockedDim,
					]}
				>
					<Text
						numberOfLines={1}
						style={[styles.levelText, { fontSize: levelFont, lineHeight: levelFont * D.levelLine }]}
					>
						{t('T_SITE_PROFILE_LEVEL')} {room.minLevel}
					</Text>
				</View>
			</View>

			{showUnlockRoomModal && (
				<UnlockRoomModal
					onClose={() => setShowUnlockRoomModal(false)}
					onUnlocked={handleRoomUnlocked}
					roomId={String(room.id)}
					roomName={room.name}
					roomUnlockCost={Number(room.unlockCost || 0)}
					minLevel={room.minLevel}
					lockImages={lockImages}
				/>
			)}
		</>
	);
};

const styles = StyleSheet.create({
	wrapper: {
		position: 'relative',
		overflow: 'visible',
	},
	lockedDim: {
		opacity: 0.55,
	},
	tablesPill: {
		position: 'absolute',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: '#00132D',
		borderColor: '#8C6B24',
		overflow: 'hidden',
	},
	tablesText: {
		color: '#FFFFFF',
		fontFamily: CARD_FONT,
		fontWeight: '700',
		textAlign: 'center',
		includeFontPadding: false,
	},
	roomTitle: {
		position: 'absolute',
		color: '#FFFFFF',
		fontFamily: CARD_FONT,
		fontWeight: '700',
		textAlign: 'center',
		textTransform: 'uppercase',
		includeFontPadding: false,
		textShadowColor: '#4D0000',
		textShadowOffset: { width: 0, height: 2 },
		textShadowRadius: 1.5,
	},
	artSlot: {
		position: 'absolute',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'visible',
	},
	artClip: {
		...StyleSheet.absoluteFillObject,
		overflow: 'hidden',
	},
	stakeRow: {
		position: 'absolute',
		flexDirection: 'row',
		alignItems: 'flex-start',
		justifyContent: 'center',
		backgroundColor: '#4E0100',
		borderColor: '#DA600E',
		elevation: 3,
	},
	stakeLabel: {
		color: '#FFFFFF',
		fontFamily: CARD_FONT,
		fontWeight: '700',
		includeFontPadding: false,
	},
	stakeValue: {
		color: '#F3DB01',
		fontFamily: CARD_FONT,
		fontWeight: '700',
		includeFontPadding: false,
	},
	levelRow: {
		position: 'absolute',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'rgba(78, 1, 0, 0.2)',
		borderTopColor: 'rgba(246, 198, 194, 0.25)',
		borderBottomColor: 'rgba(246, 198, 194, 0.14)',
		borderLeftColor: 'rgba(246, 198, 194, 0.04)',
		borderRightColor: 'rgba(246, 198, 194, 0.04)',
	},
	levelText: {
		textAlign: 'center',
		color: '#FFFFFF',
		fontFamily: CARD_FONT,
		fontWeight: '500',
		includeFontPadding: false,
	},
	lockpadContainer: {
		...StyleSheet.absoluteFillObject,
		alignItems: 'center',
		justifyContent: 'center',
	},
	lockLayer: {
		position: 'absolute',
		left: 0,
		top: 0,
	},
});

export default LobbyRoom;
